/**
 * Company Summary Table Component
 * Displays workshop summary in table format with totals row at top
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { HRSummary } from '@/types/hr';
import { formatNumberDisplay } from '@/lib/utils';

interface CompanySummaryTableProps {
  hrSummary: HRSummary;
}

const CompanySummaryTable: React.FC<CompanySummaryTableProps> = ({ hrSummary }) => {
  // Format percentage
  const formatPercentage = (value: number) => {
    return `${formatNumberDisplay(value, 1)}%`;
  };

  // Get attendance rate color
  const getAttendanceRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600 bg-green-50';
    if (rate >= 85) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Tóm tắt theo xưởng
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <Table className="w-full">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-left sm:text-center border-r font-semibold text-xs sm:text-sm px-2 sm:px-4 py-2">Xưởng</TableHead>
                <TableHead className="text-center border-r font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px] sm:min-w-[80px]">Sĩ số</TableHead>
                <TableHead className="text-center border-r font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px] sm:min-w-[80px]">Đi làm</TableHead>
                <TableHead className="text-center border-r font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[50px] sm:min-w-[70px]">Vắng</TableHead>
                <TableHead className="text-center font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[70px] sm:min-w-[100px]">Tỷ lệ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Totals row - moved to top */}
              <TableRow className="bg-blue-50 border-b-2 border-blue-200">
                <TableCell className="font-bold text-left sm:text-center border-r text-blue-800 text-xs sm:text-sm px-2 sm:px-4 py-2">
                  Tổng số
                </TableCell>
                <TableCell className="text-center border-r text-blue-800 font-bold text-sm sm:text-lg px-1 sm:px-4 py-2">
                  {formatNumberDisplay(hrSummary.totalStaff, 0)}
                </TableCell>
                <TableCell className="text-center border-r text-green-700 font-bold text-sm sm:text-lg px-1 sm:px-4 py-2">
                  {formatNumberDisplay(hrSummary.present, 0)}
                </TableCell>
                <TableCell className="text-center border-r text-red-700 font-bold text-sm sm:text-lg px-1 sm:px-4 py-2">
                  {formatNumberDisplay(hrSummary.absent, 0)}
                </TableCell>
                <TableCell className="text-center px-1 sm:px-4 py-2">
                  <Badge 
                    variant="secondary" 
                    className={`${getAttendanceRateColor(hrSummary.attendanceRate)} font-bold text-xs sm:text-sm px-1 sm:px-3 py-1`}
                  >
                    {formatPercentage(hrSummary.attendanceRate)}
                  </Badge>
                </TableCell>
              </TableRow>
              
              {/* Workshop rows */}
              {hrSummary.companies.map((company, index) => (
                <TableRow key={company.compID} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <TableCell className="font-medium text-left sm:text-center border-r text-xs sm:text-sm px-2 sm:px-4 py-2">
                    {company.compName}
                  </TableCell>
                  <TableCell className="text-center border-r text-blue-600 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">
                    {formatNumberDisplay(company.totalStaff, 0)}
                  </TableCell>
                  <TableCell className="text-center border-r text-green-600 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">
                    {formatNumberDisplay(company.present, 0)}
                  </TableCell>
                  <TableCell className="text-center border-r text-red-600 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">
                    {formatNumberDisplay(company.absent, 0)}
                  </TableCell>
                  <TableCell className="text-center px-1 sm:px-4 py-2">
                    <Badge 
                      variant="secondary" 
                      className={`${getAttendanceRateColor(company.attendanceRate)} text-xs sm:text-sm px-1 sm:px-2 py-1`}
                    >
                      {formatPercentage(company.attendanceRate)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySummaryTable;